// Test mode configuration.
// To add a new participant: add their email to TEST_USERS and add a matching
// entry to CHAPTERS with 5 parts, each containing a pre-generated screenplay.
//
// Background images go in /public/test-assets/<chapterKey>/bg1.png … bg5.png
//
// Screenplay item format (same structure the backend API returns):
//   { text, module, url, question, answer, options }
//
// module values:
//   "avatar"              – whiteboard text slide (url = "title:Title|line1|line2")
//   "quiz"                – multiple choice      (question, answer, options = "A;B;C;D")
//   "interactivequestion" – typed answer          (question, url = expected answer)

export const TEST_USERS = {
  // "student.email@example.com": "chapterKey"  OR  ["key1", "key2"]
  "sujitha1812@gmail.com": ["chapterB", "chapterC"],
  "girinath19@gmail.com": ["chapterB", "chapterC"]
};

export const CHAPTERS = {
  chapterB: {
    title: "Distance and Speed",
    parts: [
      {
        title: "Part 1: Distance and Displacement",
        backgroundImage: "/test-assets/chapterB/bg6.png",
        screenplay: [
          { module: "start", text: "Let's get started!" },
          {
            module: "avatar",
            text: "Today, we are learning about Introduction and Distance versus Displacement.",
            url: "title:Introduction and Distance vs Displacement|Understanding how objects move from one place to another|Exploring the shortest path and how we measure it",
          },
          {
            module: "genimage",
            text: "Imagine Kavitha walking to school. She has two roads to choose from — Road A is one point five kilometers and Road B is two kilometers. Which one will get her to school faster?",
            url: "https://v3b.fal.media/files/b/0a99dfdf/2Q6nQfQeZ-gD2BdTzHggg_pCOHVtu8.png",
          },
          {
            module: "avatar",
            text: "The shorter path helps Kavitha reach school earlier because it covers less distance. This simple idea leads us to the concept of distance and displacement.",
            url: "title:Shortest Path|Road A is 1.5 km|Road B is 2 km|Shorter path means less distance",
          },
          {
            module: "genimage",
            text: "Think about a leaf falling from a tree. When there is no wind, it falls straight down. When there is wind, it drifts sideways before reaching the ground. Which leaf reaches the ground first?",
            url: "https://v3b.fal.media/files/b/0a99dfdf/6GtpQJpFUovN3ifbsFRas_pS4F3IlF.png",
          },
          {
            module: "avatar",
            text: "The leaf that falls straight down reaches the ground first because it travels along the shortest path. This helps us understand that a straight line is the shortest distance between two points.",
            url: "title:Straight Line Path|Shortest distance between two points|Objects reach faster along straight line",
          },
          {
            module: "genimage",
            text: "Uma and Priya go to the playground after school. Uma visits her grandmother's house first, while Priya goes directly. Which path do you think is longer?",
            url: "https://v3b.fal.media/files/b/0a99dfdf/zsQxhAUfKjsuiecVAoINp_WCBhkvR5.png",
          },
          {
            module: "avatar",
            text: "Uma's path is longer because she takes a detour. Priya's path is shorter and more direct. This shows how distance depends on the path taken.",
            url: "title:Path Comparison|Uma takes a longer route|Priya takes a direct route|Distance depends on path",
          },
          {
            module: "interactivequestion",
            question: "What is the shortest distance between two points called?",
            text: "Let me check what you already know about this.",
            url: "straight line",
          },
          {
            module: "avatar",
            text: "When an object travels from one place to another, the total length of the path it takes is called distance. The shortest distance from the initial position to the final position is called displacement.",
            url: "title:Definitions|Distance: total length of path|Displacement: shortest distance between start and end|Both measured in metre",
          },
          {
            module: "genimage",
            text: "One of the most fascinating things about displacement is that it tells us not just how far something moved, but also in which direction. For example, if a person travels from point A to point B, displacement includes both the magnitude and direction.",
            url: "https://v3b.fal.media/files/b/0a99dfdf/oIhiWAu-1Q_7hCk8DWcbu_xMSsM01S.png",
          },
          {
            module: "avatar",
            text: "A person travels 10 kilometers along one path and 7 kilometers along another. The shortest distance between A and B is 5 kilometers east. So, the displacement is 5 kilometers east.",
            url: "title:Example of Displacement|Path 1: 10 km|Path 2: 7 km|Shortest path: 5 km east",
          },
          {
            module: "interactivequestion",
            question: "If a person walks 10 km but the shortest distance between start and end is 5 km, what is the displacement?",
            text: "Let me check what you already know about this.",
            url: "5 km",
          },
          {
            module: "avatar",
            text: "Another example: an object moves along a curved path of 120 meters, but the straight line between start and end is only 40 meters south-east. The distance is 120 meters, and the displacement is 40 meters south-east.",
            url: "title:Curved Path Example|Distance: 120 m|Displacement: 40 m south-east|Displacement shows direction",
          },
          {
            module: "genimage",
            text: "Think about a rabbit running from point A to point B on a grid. Each square is one square meter. The rabbit's total path gives the distance, while the straight line from A to B gives the displacement.",
            url: "https://v3b.fal.media/files/b/0a99dfdf/XUsVZgRLiFqsmLGlSDGce_WRconviM.png",
          },
          {
            module: "interactivequestion",
            question: "When will the distance and displacement of an object be equal?",
            text: "Let me check what you already know about this.",
            url: "When it moves in a straight line",
          },
          {
            module: "avatar",
            text: "When we represent displacement, we use positive or negative signs depending on direction. If an object moves from A to B, displacement is positive. If it moves from B to A, displacement is negative.",
            url: "title:Direction in Displacement|Positive when moving forward|Negative when moving backward|Shows direction of motion",
          },
          {
            module: "avatar",
            text: "Subha goes from her home to the playground. The total distance she travels is 400 meters, but her displacement is only 100 meters. This means she did not take a straight path.",
            url: "title:Subha's Journey|Distance: 400 m|Displacement: 100 m|Path not straight",
          },
          {
            module: "interactivequestion",
            question: "If distance is 400 meters and displacement is 100 meters, what does that tell us about the path?",
            text: "Let me check what you already know about this.",
            url: "It was not straight",
          },
          {
            module: "avatar",
            text: "If the distance and displacement are equal, it means the object moved in a straight line. If the displacement is zero but distance is not, it means the object returned to its starting point.",
            url: "title:Special Cases|Distance equals displacement: straight line|Displacement zero: returned to start",
          },
          {
            module: "avatar",
            text: "One of the most interesting applications of measuring distance is in navigation. Ships and airplanes use a special unit called nautical mile. One nautical mile equals one point eight five two kilometers.",
            url: "title:Nautical Mile|Used in aviation and sea transport|1 nautical mile = 1.852 km",
          },
          {
            module: "avatar",
            text: "The speed of ships and airplanes is measured in knots. One knot means traveling one nautical mile in one hour. This helps pilots and captains plan their routes efficiently.",
            url: "title:Knot|Unit of speed for ships and planes|1 knot = 1 nautical mile per hour",
          },
          {
            module: "video",
            text: "In this video, you'll explore how nautical miles and knots connect directly to the Earth's shape, making them ideal for navigation at sea and in the air. You'll see why sailors and pilots rely on these measurements instead of miles or kilometers, and how each nautical mile represents a precise segment of our planet's latitude and longitude.",
            url: "https://www.youtube.com/embed/aFzohPp-W2I",
          },
          {
            module: "quiz",
            question: "What is the SI unit of distance and displacement?",
            text: "Time for a quick test on what we learned.",
            answer: "metre",
            options: "metre; kilometer; nautical mile; knot",
          },
          {
            module: "quiz",
            question: "If a person travels 30 km and returns to the starting point, what is the displacement?",
            text: "Time for another quick test.",
            answer: "0 km",
            options: "30 km; 15 km; 0 km; 45 km",
          },
          {
            module: "avatar",
            text: "To summarise, distance tells us how much ground an object covers, while displacement tells us how far and in which direction it moved from its starting point. The straight line path is always the shortest.",
            url: "title:Summary|Distance: total path length|Displacement: shortest straight line|Both measured in metre",
          },
          {
            module: "recommendation",
            options: "Speed and Velocity; Acceleration; Distance-Time Graph; Centre of Gravity and Stability",
            text: "Would you like to learn more?",
            url: "https://lookaside.fbsbx.com/lookaside/crawler/media/?media_id=1330636489065306;https://cdn.kastatic.org/ka-perseus-images/ee08cfdce4bdba3844612f517e4daf28803f3c46.png;https://cdn1.byjus.com/wp-content/uploads/2023/02/Understanding-The-Application-Of-Distance-Time-Graph-3.png;https://www.schoolphysics.co.uk/age11-14/Mechanics/Statics/text/Stability_/images/3.PNG",
          },
          { module: "end", text: "Thank you for watching." },
        ],
      },
      {
        title: "Part 2: Speed and Velocity",
        backgroundImage: "/test-assets/chapterB/bg7.png",
        screenplay: [
          { module: "start", text: "Let's get started!" },
          {
            module: "avatar",
            text: "Have you ever wondered how fast a cheetah runs or how slow a tortoise moves? Today, we are going to explore the fascinating world of speed and velocity — the science behind motion!",
            url: "title:Speed and Velocity|Understanding how fast things move|Exploring rate of change of distance and displacement",
          },
          {
            module: "avatar",
            text: "Before we dive deeper, let's recall what you already know about speed from your earlier classes.",
            url: "title:Revision|Speed is the rate of change of distance|formula:Speed = Distance / Time",
          },
          {
            module: "avatar",
            text: "Speed tells us how fast an object moves. It is calculated by dividing the distance travelled by the time taken. The unit of speed is meter per second.",
            url: "title:Definition of Speed|formula:Speed = Distance / Time|Unit: metre per second (m/s)",
          },
          {
            module: "genimage",
            text: "Think about how a car moves along a straight road. If it covers equal distances in equal intervals of time, we say it has uniform speed.",
            url: "https://v3b.fal.media/files/b/0a99df79/1bGoer96KA1v_TJ2SVT4A_n1sfTSSp.png",
          },
          {
            module: "genimage",
            text: "But if the car covers unequal distances in equal intervals of time, its speed is non-uniform. This happens when the car slows down or speeds up.",
            url: "https://v3b.fal.media/files/b/0a99df79/Vvhu-H12XTPzGJ3eRbpRd_JYQWyM7f.png",
          },
          {
            module: "interactivequestion",
            question: "If a car covers equal distances in equal intervals of time, what type of speed does it have?",
            text: "Let's check what you already know about this.",
            url: "Uniform speed",
          },
          {
            module: "avatar",
            text: "Sometimes, we want to know the overall speed of a journey. That's where average speed comes in. It is the total distance travelled divided by the total time taken.",
            url: "title:Average Speed|formula:Average speed = Total distance / Total time taken",
          },
          {
            module: "avatar",
            text: "Let's look at some real-world examples of speed. A tortoise moves at zero point one meter per second, a person walking moves at one point four meter per second, and a cheetah runs at thirty-one meter per second!",
            url: "title:Know the Speed|Tortoise — 0.1 m/s|Person walking — 1.4 m/s|Cheetah running — 31 m/s",
          },
          {
            module: "genimage",
            text: "Imagine comparing all these speeds side by side — from a slow tortoise to a roaring rocket that travels at five thousand two hundred meter per second!",
            url: "https://v3b.fal.media/files/b/0a99df79/AQiM2R8FDAZPImrl2kd9Q_nBR0P7iS.png",
          },
          {
            module: "avatar",
            text: "Now that we understand speed, let's move to velocity. Velocity is the rate of change of displacement. It tells us not just how fast something moves, but also in which direction.",
            url: "title:Velocity|formula:Velocity = Displacement / Time|Unit: metre per second (m/s)",
          },
          {
            module: "interactivequestion",
            question: "What extra information does velocity give that speed does not?",
            text: "Let's check your understanding.",
            url: "Direction",
          },
          {
            module: "avatar",
            text: "An athlete runs a two hundred meter sprint in twenty-five seconds. Her distance is two hundred meters, but her displacement is only fifty meters. So her speed is eight meter per second, and her velocity is two meter per second.",
            url: "title:Example of Velocity|Distance = 200 m|Displacement = 50 m|Time = 25 s|Speed = 8 m/s|Velocity = 2 m/s",
          },
          {
            module: "genimage",
            text: "Think about how direction affects velocity. If an object changes direction, even if its speed stays the same, its velocity changes. That's why a train leaving a station has non-uniform velocity.",
            url: "https://v3b.fal.media/files/b/0a99df79/vmwH7da2GdU77ouX5Ipgm_ULZj7kAr.png",
          },
          {
            module: "interactivequestion",
            question: "If a body changes its direction but keeps the same speed, is its velocity uniform or non-uniform?",
            text: "Let's test your understanding again.",
            url: "Non-uniform velocity",
          },
          {
            module: "avatar",
            text: "When an object covers equal displacement in equal intervals of time in the same direction, it has uniform velocity. Light travelling through vacuum is a perfect example.",
            url: "title:Uniform Velocity|Equal displacement in equal time|Same direction|Example: Light in vacuum",
          },
          {
            module: "avatar",
            text: "Sometimes, we need to find the average velocity. It is the total displacement divided by the total time taken.",
            url: "title:Average Velocity|formula:Average velocity = Total displacement / Total time taken",
          },
          {
            module: "avatar",
            text: "A car travels five kilometers east and then seven kilometers west in zero point two hours. The total displacement is negative two kilometers. So the average velocity is negative ten kilometer per hour, or negative zero point two eight meter per second.",
            url: "title:Example of Average Velocity|Displacement = -2 km|Time = 0.2 h|formula:Average velocity = -10 km/h = -0.28 m/s",
          },
          {
            module: "interactivequestion",
            question: "If a car travels equal distances east and west, what will its total displacement be?",
            text: "Let's check what you already know about this.",
            url: "Zero",
          },
          {
            module: "avatar",
            text: "The triangle method helps us remember the relationship between velocity, displacement, and time. Velocity equals displacement divided by time. Time equals displacement divided by velocity. Displacement equals velocity multiplied by time.",
            url: "title:Triangle Method|formula:v = d / t|formula:t = d / v|formula:d = v × t",
          },
          {
            module: "manim",
            text: "The triangle method helps us remember the relationship between velocity, displacement, and time. Cover one quantity to find the formula for the other two.",
            url: "c1e7e960-95c1-4f4a-9bca-68d443065beb.mp4",
          },
          {
            module: "avatar",
            text: "Now, let's solve a few textbook-style problems together.",
            url: "title:Practice Problems|Car covers 100 m in 4 s|Usain Bolt covers 100 m in 9.58 s|Walking path problem with 4 m east, 2 m south, 4 m west, 2 m north",
          },
          {
            module: "avatar",
            text: "A car covers one hundred meters in four seconds. Its velocity equals displacement divided by time, which is one hundred divided by four, giving twenty-five meter per second.",
            url: "title:Example 1|formula:Velocity = 100 / 4 = 25 m/s",
          },
          {
            module: "avatar",
            text: "Usain Bolt covers one hundred meters in nine point five eight seconds. His speed equals one hundred divided by nine point five eight, which is about ten point four four meter per second. A cheetah running at thirty meter per second would easily win!",
            url: "title:Example 2|Usain Bolt speed = 10.44 m/s|Cheetah speed = 30 m/s",
          },
          {
            module: "interactivequestion",
            question: "If Usain Bolt runs at 10.44 m/s and a cheetah runs at 30 m/s, who is faster?",
            text: "Let's check your understanding again.",
            url: "Cheetah",
          },
          {
            module: "genimage",
            text: "Now imagine walking four meters east, two meters south, four meters west, and two meters north. You end up back where you started! So your total displacement is zero, even though you walked twelve meters in total.",
            url: "https://v3b.fal.media/files/b/0a99df79/b2nfRR5f7g2HzWgEJka3x_zFLgd6wk.png",
          },
          {
            module: "avatar",
            text: "Your average speed is total distance divided by time, which is twelve divided by twenty-one, or zero point five seven meter per second. But your average velocity is zero because your displacement is zero.",
            url: "title:Example 3|formula:Average speed = 12 / 21 = 0.57 m/s|Average velocity = 0",
          },
          {
            module: "manim",
            text: "You walked 12 meters around a rectangle — 4 east, 2 south, 4 west, and 2 north. Even though you covered distance, you ended where you started. Hence, your displacement is zero.",
            url: "583f65d2-fb9a-4af3-9cda-4d554dcb5195.mp4",
          },
          {
            module: "genimage",
            text: "Now that we've explored examples, let's see how speed and velocity matter in real life. Engineers use these concepts to design vehicles, athletes use them to improve performance, and meteorologists use them to track storms.",
            url: "https://v3b.fal.media/files/b/0a99df79/C7nSmXpXvF2-apfOf185c_2Bhn0Fcu.png",
          },
          {
            module: "interactivequestion",
            question: "If an object returns to its starting point, what is its displacement?",
            text: "Let's check your understanding one last time.",
            url: "Zero",
          },
          {
            answer: "metre per second (m/s)",
            module: "quiz",
            options: "kilometre per hour; metre per second (m/s); miles per hour; second per metre",
            question: "What is the SI unit of speed and velocity?",
            text: "Time for a quick quiz!",
          },
          {
            answer: "Non-uniform speed",
            module: "quiz",
            options: "Uniform speed; Non-uniform speed; Constant speed; Average speed",
            question: "If a car covers unequal distances in equal intervals of time, what type of speed does it have?",
            text: "Another quick one!",
          },
          {
            module: "avatar",
            text: "To summarise, speed tells us how fast something moves, while velocity tells us how fast and in which direction. Uniform and non-uniform motion help us describe real-world movements precisely.",
            url: "title:Summary|Speed = rate of change of distance|Velocity = rate of change of displacement|Uniform vs Non-uniform motion",
          },
          {
            module: "recommendation",
            options: "Uniform and Non-uniform Motion; Average Speed and Velocity; Distance and Displacement; Applications of Motion in Sports",
            text: "Would you like to explore more?",
            url: "https://i.ytimg.com/vi/qRR_1Gj6Kzw/hq720.jpg?sqp=-oaymwEhCK4FEIIDSFryq4qpAxMIARUAAAAAGAElAADIQj0AgKJD&rs=AOn4CLAPeTNKUxAeALG3ZqsCjLGDGkx3jQ;https://i.ytimg.com/vi/KRIMLdq8DUU/hqdefault.jpg?sqp=-oaymwEmCOADEOgC8quKqQMa8AEB-AH-A4ACgAOKAgwIABABGHIgPChDMA8=&rs=AOn4CLASNqOhfmP8qS86QM1hiCGXtJTxlg;https://cdn1.byjus.com/wp-content/uploads/2023/01/Distance-And-Displacement-1.png;https://praxilabs.com/en/blog/wp-content/uploads/2021/02/1.jpg",
          },
          { module: "end", text: "Thank you for learning with me today!" },
        ],
      },
      {
        title: "Part 3: Acceleration and Its Types",
        backgroundImage: "/test-assets/chapterB/bg8.png",
        screenplay: [
          { module: "start", text: "Let's get started!" },
          {
            module: "avatar",
            text: "Have you ever wondered why a car speeds up when you press the accelerator or slows down when you hit the brakes? Today, we'll explore the science behind that everyday experience — the concept of acceleration and its types.",
            url: "title:Acceleration and Its Types|Understanding how motion changes|Why vehicles speed up or slow down|Real-world examples from daily life",
          },
          {
            module: "avatar",
            text: "Acceleration is the rate of change of velocity. In simple words, if a body changes its speed or direction, then it is said to be accelerated.",
            url: "title:Definition of Acceleration|Change in speed or direction means acceleration|formula:a = (v - u) / t|SI unit is meter per second squared",
          },
          {
            module: "genimage",
            text: "Think about how a car starts from rest and gradually picks up speed. That change in velocity over time is what we call acceleration.",
            url: "https://v3b.fal.media/files/b/0a99dd69/9MVcvMULw-7JA6gY5YQYk_PX32diQh.png",
          },
          {
            module: "avatar",
            text: "Let's look at a simple example. A car at rest starts to travel in a straight line and reaches a velocity of 12 meters per second in 4 seconds. Its acceleration is calculated as final velocity minus initial velocity divided by time. That's (12 minus 0) divided by 4, which equals 3 meters per second squared.",
            url: "title:Example of Acceleration|Initial velocity u = 0 m/s|Final velocity v = 12 m/s|Time t = 4 s|formula:a = (v - u) / t = 3 m/s²",
          },
          {
            module: "interactivequestion",
            question: "If a car's velocity changes from 0 to 20 m/s in 5 seconds, what is its acceleration?",
            text: "Let me check what you already know about this.",
            url: "4 m/s²",
          },
          {
            module: "genimage",
            text: "One of the most fascinating things about acceleration is how it connects to animals too. The cheetah, for example, can go from 0 to 20 meters per second in just 2 seconds. That's an acceleration of 10 meters per second squared — much faster than most cars!",
            url: "https://v3b.fal.media/files/b/0a99dd69/CWaBkl0nV0sYHCkHrPw7l_lHoNKxTw.png",
          },
          {
            module: "avatar",
            text: "Now that we understand what acceleration means, let's explore its types. The first one is positive acceleration.",
            url: "title:Positive Acceleration|Velocity increases with time|Object speeds up|Example: train gaining speed",
          },
          {
            module: "genimage",
            text: "Imagine a train starting from rest and gradually increasing its speed. Between points A and B, its velocity changes from 0 to 6 meters per second in 10 seconds. The acceleration is 0.6 meters per second squared.",
            url: "https://v3b.fal.media/files/b/0a99dd69/hwl9K4cESYA7y58MRW4cn_FtVdOiG4.png",
          },
          {
            module: "interactivequestion",
            question: "What happens to velocity during positive acceleration?",
            text: "Let me check what you already know about this.",
            url: "It increases with time",
          },
          {
            module: "avatar",
            text: "But sometimes, objects slow down instead of speeding up. That's when we talk about negative acceleration, also called deceleration or retardation.",
            url: "title:Negative Acceleration|Velocity decreases with time|Also called deceleration or retardation|Example: golf ball slowing down",
          },
          {
            module: "avatar",
            text: "For example, a golf ball rolling in a straight line changes its velocity from 8 meters per second to 2 meters per second in 10 seconds. Using the same formula, acceleration equals (2 minus 8) divided by 10, which gives negative 0.6 meters per second squared. The negative sign shows that the object is slowing down.",
            url: "title:Example of Deceleration|Initial velocity u = 8 m/s|Final velocity v = 2 m/s|Time t = 10 s|formula:a = (v - u) / t = -0.6 m/s²",
          },
          {
            module: "genimage",
            text: "Think about how a car comes to a stop at a red light. The driver applies brakes, and the car's velocity decreases steadily — that's deceleration in action.",
            url: "https://v3b.fal.media/files/b/0a99dd69/HToVpEaP6nEm8BWVVyZc3_qoYiVgHw.png",
          },
          {
            module: "interactivequestion",
            question: "What does a negative acceleration value indicate?",
            text: "Let me check what you already know about this.",
            url: "The object is slowing down",
          },
          {
            module: "avatar",
            text: "Now, let's move to uniform acceleration. An object undergoes uniform acceleration when the change in its velocity for every unit of time is the same.",
            url: "title:Uniform Acceleration|Equal change in velocity per unit time|Example: bus increasing speed evenly|Constant acceleration",
          },
          {
            module: "genimage",
            text: "For instance, if a bus increases its velocity by 20 meters per second every second, its acceleration is uniform and equals 20 meters per second squared.",
            url: "https://v3b.fal.media/files/b/0a99dd69/4oa4Hyls747DjLirSx6J3_DFLeFnUf.png",
          },
          {
            module: "interactivequestion",
            question: "If an object's velocity increases by the same amount every second, what type of acceleration is it?",
            text: "Let me check what you already know about this.",
            url: "Uniform acceleration",
          },
          {
            module: "avatar",
            text: "Sometimes, the change in velocity is not the same for every second. That's called non-uniform acceleration.",
            url: "title:Non-uniform Acceleration|Unequal change in velocity per unit time|Example: car moving through traffic|Acceleration varies",
          },
          {
            module: "avatar",
            text: "For example, if a car's velocity changes as 0, 10, 40, 60, 70, and 50 meters per second at times 0, 1, 2, 3, 4, and 5 seconds, the change in velocity is not the same each second. So, the car is undergoing non-uniform acceleration.",
            url: "title:Example of Non-uniform Acceleration|Velocity changes irregularly|formula:Change in velocity = v - u|Acceleration not constant",
          },
          {
            module: "genimage",
            text: "Think about driving through city traffic — sometimes you speed up, sometimes you slow down. That's a perfect real-world example of non-uniform acceleration.",
            url: "https://v3b.fal.media/files/b/0a99dd69/2oF8mHiz-SFLyV3MwpcIT_qkXRXmyJ.png",
          },
          {
            module: "manim",
            text: "As the car moves, its velocity vectors grow longer showing positive acceleration, then shorten showing negative acceleration. Each time label marks how velocity changes over time. Acceleration controls how fast the car speeds up or slows down.",
            url: "d614844f-6077-4406-bc72-6f25bb337fa2.mp4",
          },
          {
            module: "manim",
            text: "Here we see that the uniformly accelerated object's velocity increases steadily, forming a straight line, while the non-uniform one fluctuates, forming a curve. This shows how constant acceleration differs from varying acceleration.",
            url: "c9e2c4df-8068-4c08-8322-22b096715c9e.mp4",
          },
          {
            answer: "0.6 m/s²",
            module: "quiz",
            options: "0.3 m/s²; 0.6 m/s²; 1.2 m/s²; 6 m/s²",
            question: "If a train's velocity increases from 0 to 6 m/s in 10 s, what is its acceleration?",
            text: "Time for a quick test on what we've learned so far.",
          },
          {
            module: "video",
            text: "In this video, you'll see how acceleration shows up all around you — in speeding cars, sprinting athletes, and thrilling roller coasters. You'll learn what it really means for something to accelerate, how to calculate it, and how to figure out its direction. Watch closely to connect the concept to real-life motion you experience every day.",
            url: "https://www.youtube.com/embed/Z5aKC-zoLOk",
          },
          {
            module: "avatar",
            text: "To summarise, acceleration is the rate of change of velocity. It can be positive when an object speeds up, negative when it slows down, uniform when the change is steady, and non-uniform when the change varies. Understanding acceleration helps us describe motion accurately — from vehicles and sports to natural movements.",
            url: "title:Summary|Acceleration = rate of change of velocity|Positive, negative, uniform, non-uniform types|Essential for understanding motion",
          },
          {
            module: "recommendation",
            options: "Velocity and Speed; Motion Graphs; Newton's Laws of Motion; Real-life Applications of Acceleration",
            text: "Would you like to learn more?",
            url: "https://i.ytimg.com/vi/izlXp0RRKYY/maxresdefault.jpg;https://lh4.googleusercontent.com/xYGdjKRv8eUJBaL7czKpCvNrMu6T6mVUje6ajveAu2j1OOhmz3yT6eEv3R0Rm6vozAYgwMh7gJ8uuKHvW7OFtJ6zoLo51QbUKEpHVcZW0efCWov4_dJar4z-4bBw-HgdkhkVgn4R;https://lookaside.fbsbx.com/lookaside/crawler/media/?media_id=1137515304830982;https://images.examples.com/wp-content/uploads/2022/11/Newtons-Second-Law-of-Motion-Examples-In-Everyday-Life.gif",
          },
          { module: "end", text: "Thank you for watching." },
        ],
      }
    ],
  },
  chapterC: {
    title: "Matter and Materials",
    parts: [
      {
        title: "Part 1: Matter, Materials, and States of Matter",
        backgroundImage: "/test-assets/chapterC/bg1.png",
        screenplay: [
          { module: "start", text: "Let's get started!" },
          {
            module: "avatar",
            text: "You already know from our lessons on gravity and motion that everything around us has mass and occupies space. Today, we'll explore what all those 'things' really are — the matter and materials that make up our world.",
            url: "title:Matter, Materials and States of Matter|Everything around us is made of matter|Matter has mass and occupies space|Matter exists in different states"
          },
          {
            module: "avatar",
            text: "Think about your surroundings — your pen, notebook, water bottle, and even the air you breathe. All these are examples of matter. Matter can exist in three physical states: solid, liquid, and gas.",
            url: "title:Introduction to Matter|Matter is anything that has mass and occupies space|Exists in three states: solid, liquid, gas|Made up of molecules and atoms"
          },
          {
            module: "genimage",
            text: "One of the most fascinating things about matter is how its molecules behave differently in each state. In solids, molecules are tightly packed; in liquids, they are loosely packed; and in gases, they are very far apart.",
            url: "https://v3b.fal.media/files/b/0a99b2ac/pFuTrfw6tpjLgYWfcF2T8_bZmcsBaV.png"
          },
          {
            module: "interactivequestion",
            question: "What do we call anything that has mass and occupies space?",
            text: "Let me check what you already know about this.",
            url: "matter"
          },
          {
            module: "avatar",
            text: "Solids are incompressible and have definite shape, size, and volume. That's why a wooden block or a pencil keeps its shape no matter where you place it.",
            url: "title:Solid State|Molecules are closely packed|Incompressible|Definite shape, size, and volume"
          },
          {
            module: "genimage",
            text: "Think about how a solid behaves when you try to squeeze it. The molecules are packed so tightly that they resist compression. This is why you cannot easily compress a metal rod or a brick.",
            url: "https://v3b.fal.media/files/b/0a99b2ac/QXaVxkGQ1RO_v0TaM_GaE_xqnzvw9g.png"
          },
          {
            module: "avatar",
            text: "Liquids are different. Their molecules are loosely packed, so they can flow and take the shape of their container. They have definite volume but no definite shape.",
            url: "title:Liquid State|Molecules loosely packed|Negligibly compressible|Definite volume but no definite shape"
          },
          {
            module: "genimage",
            text: "Imagine pouring water into a glass, then into a bowl. The volume remains the same, but the shape changes. That's the beauty of liquids — they adapt to their container.",
            url: "https://v3b.fal.media/files/b/0a99b2ac/gQsnUlBLN1OsyGAScF72f_rxcJtH3i.png"
          },
          {
            module: "interactivequestion",
            question: "Which state of matter takes the shape of its container?",
            text: "Let me check what you already know about this.",
            url: "liquid"
          },
          {
            module: "avatar",
            text: "Gases are even more interesting. Their molecules are very loosely packed, making them highly compressible. They have neither definite shape nor definite volume.",
            url: "title:Gaseous State|Molecules very loosely packed|Highly compressible|No definite shape or volume"
          },
          {
            module: "genimage",
            text: "Think about how air fills a balloon. The gas molecules spread out to fill the entire space available. If you squeeze the balloon, the gas compresses easily — that's the property of gases.",
            url: "https://v3b.fal.media/files/b/0a99b2ac/nZ-8aNMB2AgrFHtV4tOsQ_HEuclAvh.png"
          },
          {
            answer: "gas",
            module: "quiz",
            options: "solid; liquid; gas; plasma",
            question: "Which state of matter is highly compressible?",
            text: "Time for a quick test on states of matter."
          },
          {
            module: "avatar",
            text: "Now that we understand the states of matter, let's talk about materials. A material is a mixture of substances that constitute an object. Materials can be pure or impure, natural or man-made.",
            url: "title:Materials|Mixture of substances|Constitute an object|Can be pure or impure, natural or man-made"
          },
          {
            module: "genimage",
            text: "Think about cotton, wood, and plastic. Cotton and wood are natural materials, while plastic is man-made. We use both types to make things we need every day.",
            url: "https://v3b.fal.media/files/b/0a99b2ac/p4gwO6pJtGpvWEKuX-3MK_XwjpH1oc.png"
          },
          {
            module: "interactivequestion",
            question: "Which of these is a man-made material: cotton, wood, or plastic?",
            text: "Let me check what you already know about this.",
            url: "plastic"
          },
          {
            module: "avatar",
            text: "Materials are required to get the things needed for our daily life — food, dress, and many other goods. Natural and man-made materials are transformed to produce these things.",
            url: "title:Transformation of Materials|Used to make food, dress, and goods|Natural and man-made materials are transformed"
          },
          {
            module: "genimage",
            text: "Think about how cotton from plants becomes fabric for clothes, or how wheat grains become bread. These transformations show how materials are processed to meet our needs.",
            url: "https://v3b.fal.media/files/b/0a99b2ac/X1OKAyAMEZAiHTCXIIFhy_fH3oOKRc.png"
          },
          {
            module: "video",
            text: "In this video, you'll follow the journey of Merino wool from Australian farms to the creation of soft, durable fabrics and garments. See how raw fleece is cleaned, carded, combed, spun, and woven or knitted into textiles used worldwide. Understand how both traditional craftsmanship and modern technology shape the wool we wear every day.",
            url: "https://www.youtube.com/embed/YwRbyTCqOQY"
          },
          {
            module: "interactivequestion",
            question: "What do we call a mixture of substances that make up an object?",
            text: "Let me check what you already know about this.",
            url: "material"
          },
          {
            module: "avatar",
            text: "Our needs have increased in modern days, and we use a number of things in daily life. Some come from nature, and some are manufactured artificially. Understanding matter and materials helps us appreciate how science supports our everyday living.",
            url: "title:Why It Matters|We use natural and artificial materials|Science helps us understand and create useful things"
          },
          {
            module: "genimage",
            text: "One of the most fascinating things about studying matter is realizing that everything — from the smallest atom to the largest planet — follows the same basic principles. The way molecules pack together determines whether something is solid, liquid, or gas.",
            url: "https://v3b.fal.media/files/b/0a99b2ac/1Qq04TQbpWrzvyEimNgh7_reXYUgpm.png"
          },
          {
            answer: "cotton",
            module: "quiz",
            options: "plastic; cotton; nylon; polyester",
            question: "Which of the following is a natural material?",
            text: "Time for a quick test on materials."
          },
          {
            module: "avatar",
            text: "To summarise, matter is anything that has mass and occupies space. It exists in three states — solid, liquid, and gas. Materials are mixtures of substances that make up objects, and they can be natural or man-made.",
            url: "title:Summary|Matter has mass and occupies space|Three states: solid, liquid, gas|Materials are mixtures of substances|Can be natural or man-made"
          },
          {
            module: "recommendation",
            options: "Manufacturing of fabrics; Why things float or sink; Varieties of grains and food products; Applications of materials in daily life",
            text: "Would you like to learn more?",
            url: "http://fabricwholesaledirect.com/cdn/shop/articles/sKdBwZjt7g8hl76YZ0JjMUfYoDVI7zv71596459673_e78adfb0-61fb-49ff-b65b-c92c48d9469b.jpg?v=1612497939;https://sciencenotes.org/wp-content/uploads/2023/03/Things-That-Float-or-Sink.png;https://www.eufic.org/en/images/uploads/whats-in-food/Whole_grain_-_article_WholeGrainVariety.png;https://www.americanpiezo.com/wp-content/uploads/2023/11/header-1.webp"
          },
          { module: "end", text: "Thank you for watching." }
        ]
      },
      {
        title: "Part 2: Natural Fibres and Cotton to Fabric",
        backgroundImage: "/test-assets/chapterC/bg2.png",
        screenplay: [
          { module: "start", text: "Let's get started!" },
          {
            module: "avatar",
            text: "Have you ever wondered how the soft cotton clothes you wear start their journey from a tiny plant? Today, we'll explore the fascinating story of Natural Fibres and how Cotton becomes Fabric.",
            url: "title:Natural Fibres and Cotton to Fabric|Fibre is a thin thread of natural or artificial substance|Used to make cloth with looms or weaving machines|We get fibres from plants and animals"
          },
          {
            module: "interactivequestion",
            question: "What do we call the thin thread-like material used to make cloth?",
            text: "Before we dive deeper, let's recall what you already know about fibres.",
            url: "Fibre"
          },
          {
            module: "avatar",
            text: "One of the most interesting things about fibres is that they can come from both nature and human-made sources. Natural fibres come from plants and animals, while synthetic fibres are made by humans through chemical synthesis.",
            url: "title:Types of Fibres|Natural fibres from plants and animals|Synthetic fibres made by humans|Examples include cotton, jute, wool, silk, rayon, nylon"
          },
          {
            module: "genimage",
            text: "Think about how cotton, jute, and wool feel different when you touch them. That's because each fibre has its own origin and structure. Cotton and jute come from plants, while wool and silk come from animals.",
            url: "https://v3b.fal.media/files/b/0a99b37e/1hvS9LB0yjVRHv6LRSMfz_WXJZ6Bbo.png"
          },
          {
            module: "interactivequestion",
            question: "Which of these is an animal fibre: cotton, jute, wool, or flax?",
            text: "Let's check your understanding so far.",
            url: "wool"
          },
          {
            module: "avatar",
            text: "Now, let's focus on cotton — one of the most widely used natural fibres. A cotton plant is a bushy plant about five to six feet high. It grows well in black soil and alluvial soil.",
            url: "title:Cotton Plant|Bushy plant about 5 to 6 feet high|Grows well in black and alluvial soil|Bears small green pods called cotton balls"
          },
          {
            module: "genimage",
            text: "Imagine a field full of cotton plants swaying in the breeze. Each plant bears small green pods called cotton balls. Inside these balls are seeds covered with white fibres. When the balls mature, they burst open, revealing the fluffy white cotton.",
            url: "https://v3b.fal.media/files/b/0a99b37f/zQ5kBGiu_n3l8SyghfFix_2VuwWrG3.png"
          },
          {
            module: "video",
            text: "In this video, you'll watch how cotton is carefully picked from the fields and gathered for processing. Notice the steps involved in collecting the fluffy fibers and how workers handle the plants to keep the cotton clean and ready for use.",
            url: "https://www.youtube.com/embed/oMvea3PtOLE"
          },
          {
            module: "avatar",
            text: "Cotton is usually hand-picked from the plants. After picking, the next step is ginning — separating the raw fibres from the seeds.",
            url: "title:Ginning|Separates raw fibres from seeds|Fibrous material left is called lint|Lint is thinned and pressed into balls"
          },
          {
            module: "genimage",
            text: "Think about how the seeds and fibres are mixed together inside the cotton ball. Ginning helps remove the seeds, leaving behind lint — the soft fibrous material that will later become yarn.",
            url: "https://v3b.fal.media/files/b/0a99b37e/eCVaRoj6PjfhghGOs9Oco_Nku29cAB.png"
          },
          {
            module: "interactivequestion",
            question: "What is the fibrous material left after separating cotton seeds called?",
            text: "Let's check what you remember.",
            url: "Lint"
          },
          {
            module: "avatar",
            text: "After ginning, the lint is cleaned further. Short fibres and impurities are removed by combing. This ensures that only long, smooth fibres are used for spinning.",
            url: "title:Combing|Removes short fibres and impurities|Prepares lint for spinning"
          },
          {
            module: "avatar",
            text: "Now comes spinning — the process of making yarn from lint or fibre. Spinning is done on a large scale using spinning machines.",
            url: "title:Spinning|Converts lint into yarn|Done on spinning machines|Forms continuous threads for weaving"
          },
          {
            module: "genimage",
            text: "Picture the spinning machines twisting the fibres together to form long, strong threads. These threads are called yarn, and they are the building blocks of fabric.",
            url: "https://v3b.fal.media/files/b/0a99b37f/W_kxe9qb7wBOPNLa1z54R_mS5oxvax.png"
          },
          {
            module: "interactivequestion",
            question: "What is the process of making yarn from lint called?",
            text: "Let's test your understanding.",
            url: "Spinning"
          },
          {
            module: "avatar",
            text: "Once we have yarn, we can make fabric through weaving or knitting. Weaving involves interlacing two sets of yarn at right angles on a loom. Knitting uses a single yarn looped continuously to make fabric.",
            url: "title:Yarn to Fabric|Weaving uses two sets of yarn|Knitting uses one continuous yarn|Looms can be hand-operated or power-operated"
          },
          {
            module: "genimage",
            text: "Think about how your sweater and bedsheet are made differently. Sweaters are knitted, while bedsheets are woven. Both methods transform yarn into fabric we use every day.",
            url: "https://v3b.fal.media/files/b/0a99b37f/mC1bId0kBc4OTPY2DDJVC_WRLenjwU.png"
          },
          {
            module: "video",
            text: "In this video, you'll watch a loom in motion and see how threads are skillfully interlaced to create fabric. Notice how each movement of the shuttle and heddles works together to form patterns. It's a fascinating look at the rhythm and precision behind the ancient craft of weaving.",
            url: "https://www.youtube.com/embed/LUl1lcpmiSA"
          },
          {
            module: "interactivequestion",
            question: "Which process uses two sets of yarn to make fabric?",
            text: "Let's check your understanding again.",
            url: "Weaving"
          },
          {
            module: "avatar",
            text: "Cotton fabric has many uses. It is used to make textiles and garments, fillers in pillows and mattresses, surgical bandages, and household items like dhotis, sarees, bedsheets, and table cloths.",
            url: "title:Uses of Cotton|Textiles and garments|Fillers in pillows and mattresses|Surgical bandages|Household items like sarees and bedsheets"
          },
          {
            module: "genimage",
            text: "Think about how cotton touches your life every day — from the clothes you wear to the bedsheets you sleep on. Its softness, breathability, and versatility make it one of the most loved natural fibres.",
            url: "https://v3b.fal.media/files/b/0a99b37f/6vQTRMG9cU4PfnI2XRjas_Fg2rRj5X.png"
          },
          {
            answer: "Ginning",
            module: "quiz",
            options: "Spinning; Weaving; Ginning; Knitting",
            question: "What process separates cotton fibres from seeds?",
            text: "Time for a quick quiz!"
          },
          {
            module: "avatar",
            text: "To summarise, the journey of cotton from plant to fabric follows a clear flow: Cotton balls → Ginning → Cotton fibre → Lint → Spinning → Yarn → Weaving or Knitting → Fabric.",
            url: "title:Summary|Flow of cotton to fabric|Shows transformation from plant to cloth|Important for textile industry"
          },
          {
            module: "recommendation",
            options: "Jute and its processing; Wool and silk fibres; Synthetic fibres like nylon and rayon; Textile weaving techniques",
            text: "Would you like to explore more?",
            url: "https://imperialjute.com/wp-content/uploads/2025/04/Jute-Manufacturing-Process_-From-Farm-to-Fabric.jpg;https://cdn.shopify.com/s/files/1/0503/0163/2711/files/silk_and_wool_480x480.jpg?v=1706200712;https://szoneierfabrics.com/wp-content/uploads/2025/05/4efac965-16f3-4c83-b294-25b79ff9a9d7-1-1024x1024.jpg;https://www.mayanhands.org/cdn/shop/files/Santiago_Atitlan_brocade_weaving_swatch_red_1600x.jpg?v=1688513263"
          },
          { module: "end", text: "Thank you for learning with me today!" }
        ]
      }
    ]
  }
};
